import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils";
import { Trash2, RotateCcw } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * CS 21.73 — Designs admin view. Lists designs per franchise, supports
 * search and soft-delete. Uses its own /api/designs endpoints; never
 * touches the physical stock Trash flow.
 */
export default function Designs() {
  const [franchises, setFranchises] = useState([]);
  const [franchiseId, setFranchiseId] = useState("");
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("active");
  const [trash, setTrash] = useState([]);

  const refresh = useCallback(async () => {
    setBusy(true);
    try {
      const q = new URLSearchParams();
      if (franchiseId) q.set("franchise_id", franchiseId);
      if (search) q.set("search", search);
      const r = await api.get(`/designs?${q.toString()}`);
      setItems(r.data || []);
      const t = await api.get("/designs-trash");
      const filtered = franchiseId ? (t.data || []).filter((d) => d.source_id === franchiseId) : (t.data || []);
      setTrash(filtered);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  }, [franchiseId, search]);

  useEffect(() => {
    api.get("/franchises").then((r) => setFranchises(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const softDelete = async (id) => {
    if (!window.confirm("Move this design to Trash?")) return;
    try {
      await api.delete(`/designs/${id}`);
      toast.success("Design moved to Trash");
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const restore = async (id) => {
    try {
      await api.post(`/designs/${id}/restore`);
      toast.success("Design restored");
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const visible = tab === "active" ? items : trash;

  return (
    <div className="space-y-6" data-testid="designs-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900">Designs</h1>
          <p className="text-sm text-slate-500 mt-1">
            Style-keyed catalog entries. Isolated from physical stock — soft-delete moves to a
            design-only Trash.
          </p>
        </div>
        <Link to="/upload-designs" className="inline-flex items-center rounded bg-slate-900 px-3 py-1.5 text-sm text-white" data-testid="link-upload-designs">
          + Upload Designs
        </Link>
      </div>

      <div className="rounded-lg border bg-white p-4 flex flex-wrap gap-3">
        <Select value={franchiseId} onValueChange={setFranchiseId}>
          <SelectTrigger className="w-72" data-testid="designs-franchise-filter">
            <SelectValue placeholder="All franchises" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__" data-testid="designs-franchise-opt-all">All franchises</SelectItem>
            {franchises.map((f) => (
              <SelectItem key={f.id} value={f.id} data-testid={`designs-franchise-opt-${f.id}`}>
                {f.name} ({f.design_count || 0})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search style / jewel code…"
          className="w-72"
          data-testid="designs-search"
        />
        <div className="ml-auto flex gap-2">
          <Button variant={tab === "active" ? "default" : "outline"} size="sm" onClick={() => setTab("active")} data-testid="designs-tab-active">
            Active ({items.length})
          </Button>
          <Button variant={tab === "trash" ? "default" : "outline"} size="sm" onClick={() => setTab("trash")} data-testid="designs-tab-trash">
            Trash ({trash.length})
          </Button>
        </div>
      </div>

      <div className="rounded-lg border bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-slate-600">
            <tr>
              <th className="px-3 py-2">Style No</th>
              <th className="px-3 py-2">Catalog</th>
              <th className="px-3 py-2">Category</th>
              <th className="px-3 py-2">Purity</th>
              <th className="px-3 py-2">Net Wt</th>
              <th className="px-3 py-2">Ref. Jewel Code</th>
              <th className="px-3 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {busy && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400">Loading…</td></tr>
            )}
            {!busy && visible.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400" data-testid="designs-empty">No designs</td></tr>
            )}
            {!busy && visible.map((d) => (
              <tr key={d.id} className="border-t" data-testid={`design-row-${d.id}`}>
                <td className="px-3 py-2 font-mono">{d.style_no}</td>
                <td className="px-3 py-2">{d.source_name}</td>
                <td className="px-3 py-2">{d.category}</td>
                <td className="px-3 py-2">{d.purity}</td>
                <td className="px-3 py-2">{d.net_weight}</td>
                <td className="px-3 py-2 text-slate-400">{d.jewel_code_ref || "—"}</td>
                <td className="px-3 py-2 text-right">
                  {tab === "active" ? (
                    <Button variant="ghost" size="sm" onClick={() => softDelete(d.id)} data-testid={`design-delete-${d.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  ) : (
                    <Button variant="ghost" size="sm" onClick={() => restore(d.id)} data-testid={`design-restore-${d.id}`}>
                      <RotateCcw className="w-4 h-4" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
